import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users,
  Building2,
  MessageSquare,
  ThumbsUp,
  Minus,
  ThumbsDown,
  Filter,
  X
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
  <div className="stats-card card-hover" data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-gray-500 uppercase tracking-wider" style={{ fontFamily: 'Montserrat' }}>
          {title}
        </p>
        <p className="text-3xl font-bold mt-2 text-[#212121]">{value}</p>
        {subtitle && <p className="text-sm text-gray-400 mt-1">{subtitle}</p>}
      </div>
      <div className={`w-12 h-12 flex items-center justify-center ${color}`}>
        <Icon className="w-6 h-6" />
      </div>
    </div>
  </div>
);

const NPSBreakdown = ({ title, icon: Icon, promoters, passives, detractors, total }) => {
  const getPercentage = (value) => total > 0 ? Math.round((value / total) * 100) : 0;

  return (
    <div className="stats-card" data-testid="nps-breakdown">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-5 h-5 text-[#B5985A]" />
        <h3 className="font-bold text-lg text-[#212121]" style={{ fontFamily: 'Montserrat' }}>
          {title}
        </h3>
      </div>
      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <ThumbsUp className="w-4 h-4 text-green-500" />
              <span className="text-sm font-medium">Promotores (9-10)</span>
            </div>
            <span className="text-sm font-bold">{promoters} ({getPercentage(promoters)}%)</span>
          </div>
          <div className="h-2 bg-gray-100 overflow-hidden">
            <div
              className="h-full bg-green-500"
              style={{ width: `${getPercentage(promoters)}%` }}
            />
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Minus className="w-4 h-4 text-yellow-500" />
              <span className="text-sm font-medium">Passivos (7-8)</span>
            </div>
            <span className="text-sm font-bold">{passives} ({getPercentage(passives)}%)</span>
          </div>
          <div className="h-2 bg-gray-100 overflow-hidden">
            <div
              className="h-full bg-yellow-500"
              style={{ width: `${getPercentage(passives)}%` }}
            />
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <ThumbsDown className="w-4 h-4 text-red-500" />
              <span className="text-sm font-medium">Detratores (0-6)</span>
            </div>
            <span className="text-sm font-bold">{detractors} ({getPercentage(detractors)}%)</span>
          </div>
          <div className="h-2 bg-gray-100 overflow-hidden">
            <div
              className="h-full bg-red-500"
              style={{ width: `${getPercentage(detractors)}%` }}
            />
          </div>
        </div>
      </div>
      <div className="mt-6 pt-4 border-t border-gray-100">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">NPS Score</span>
          <span className="text-2xl font-bold text-[#B5985A]">
            {total > 0 ? getPercentage(promoters) - getPercentage(detractors) : 0}
          </span>
        </div>
      </div>
    </div>
  );
};

export default function AdminDashboard() {
  useAuth(); // ensure authenticated
  const [evaluations, setEvaluations] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterAgent, setFilterAgent] = useState('');
  const [filterAgency, setFilterAgency] = useState('');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [evalsRes, agenciesRes, agentsRes] = await Promise.all([
        axios.get(`${API}/evaluations`),
        axios.get(`${API}/agencies`),
        axios.get(`${API}/agents`),
      ]);
      setEvaluations(evalsRes.data);
      setAgencies(agenciesRes.data);
      setAgents(agentsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const hasActiveFilters = filterStartDate || filterEndDate || filterAgent || filterAgency;

  const clearFilters = () => {
    setFilterStartDate('');
    setFilterEndDate('');
    setFilterAgent('');
    setFilterAgency('');
  };

  const filteredEvaluations = useMemo(() => {
    return evaluations.filter((ev) => {
      if (filterStartDate) {
        const evDate = new Date(ev.created_at).toISOString().split('T')[0];
        if (evDate < filterStartDate) return false;
      }
      if (filterEndDate) {
        const evDate = new Date(ev.created_at).toISOString().split('T')[0];
        if (evDate > filterEndDate) return false;
      }
      if (filterAgent && ev.agent_id !== filterAgent) return false;
      if (filterAgency && ev.agency_id !== filterAgency) return false;
      return true;
    });
  }, [evaluations, filterStartDate, filterEndDate, filterAgent, filterAgency]);

  const stats = useMemo(() => {
    const total = filteredEvaluations.length;
    const agencyScores = filteredEvaluations.map(e => e.agency_nps || 0);
    const agentScores = filteredEvaluations.map(e => e.agent_nps || 0);

    const avgAgency = total > 0 ? agencyScores.reduce((a, b) => a + b, 0) / total : 0;
    const avgAgent = total > 0 ? agentScores.reduce((a, b) => a + b, 0) / total : 0;

    return {
      total_evaluations: total,
      average_agency_nps: Math.round(avgAgency * 10) / 10,
      average_agent_nps: Math.round(avgAgent * 10) / 10,
      agency_promoters: agencyScores.filter(n => n >= 9).length,
      agency_passives: agencyScores.filter(n => n >= 7 && n <= 8).length,
      agency_detractors: agencyScores.filter(n => n <= 6).length,
      agent_promoters: agentScores.filter(n => n >= 9).length,
      agent_passives: agentScores.filter(n => n >= 7 && n <= 8).length,
      agent_detractors: agentScores.filter(n => n <= 6).length,
      total_agents: agents.filter(a => a.active).length,
    };
  }, [filteredEvaluations, agents]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#B5985A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-[#212121]" style={{ fontFamily: 'Montserrat' }}>
          Dashboard
        </h1>
        <p className="text-gray-500 mt-1">Visão geral das avaliações e métricas</p>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-100 p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-[#B5985A]" />
          <span className="label-uppercase">Filtros</span>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="ml-auto flex items-center gap-1 text-xs text-gray-500 hover:text-red-500"
            >
              <X className="w-3 h-3" />
              Limpar filtros
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Data início</label>
            <Input
              type="date"
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
              className="h-9 border-gray-200 rounded-none text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Data fim</label>
            <Input
              type="date"
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
              className="h-9 border-gray-200 rounded-none text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Agência</label>
            <select
              value={filterAgency}
              onChange={(e) => setFilterAgency(e.target.value)}
              className="w-full h-9 border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:border-[#B5985A]"
            >
              <option value="">Todas</option>
              {agencies.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Consultor</label>
            <select
              value={filterAgent}
              onChange={(e) => setFilterAgent(e.target.value)}
              className="w-full h-9 border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:border-[#B5985A]"
            >
              <option value="">Todos</option>
              {agents
                .filter((ag) => !filterAgency || ag.agency_id === filterAgency)
                .map((ag) => (
                  <option key={ag.id} value={ag.id}>{ag.name}</option>
                ))}
            </select>
          </div>
        </div>
        {hasActiveFilters && (
          <p className="text-xs text-gray-400 mt-3">
            {filteredEvaluations.length} de {evaluations.length} avaliações
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Avaliações"
          value={stats.total_evaluations}
          icon={MessageSquare}
          color="bg-[#B5985A] text-white"
        />
        <StatCard
          title="NPS Agência"
          value={stats.average_agency_nps.toFixed(1)}
          icon={Building2}
          color="bg-[#212121] text-white"
          subtitle="Média de 0 a 10"
        />
        <StatCard
          title="NPS Consultores"
          value={stats.average_agent_nps.toFixed(1)}
          icon={Users}
          color="bg-green-500 text-white"
          subtitle="Média de 0 a 10"
        />
        <StatCard
          title="Consultores"
          value={stats.total_agents}
          icon={Users}
          color="bg-blue-500 text-white"
          subtitle="Activos"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <NPSBreakdown
          title="NPS Agência"
          icon={Building2}
          promoters={stats.agency_promoters}
          passives={stats.agency_passives}
          detractors={stats.agency_detractors}
          total={stats.total_evaluations}
        />

        <NPSBreakdown
          title="NPS Consultores"
          icon={Users}
          promoters={stats.agent_promoters}
          passives={stats.agent_passives}
          detractors={stats.agent_detractors}
          total={stats.total_evaluations}
        />
      </div>
    </div>
  );
}
